// src/GDB/components/CSVDatasetUpload.jsx
/**
 * CSV Dataset Upload Component (Improved)
 * - Sends delimiter correctly for preview as query param
 * - Shows real backend error detail for 400/422/500 responses
 * - Handles TSV delimiter + state timing correctly
 */

import { useState, useCallback } from "react";

async function readErrorDetail(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();

  // Try JSON first if it looks like JSON
  if (ct.includes("application/json")) {
    try {
      const j = await res.json();
      return j?.detail || j?.message || JSON.stringify(j);
    } catch {
      // fallthrough to text
    }
  }

  // fallback: plain text/html
  try {
    const t = await res.text();
    return t?.trim() || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

export default function CSVDatasetUpload({ onSuccess, onClose }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [delimiter, setDelimiter] = useState(",");

  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Preview the CSV file
  const handlePreview = async (fileToPreview = file, delimiterOverride = delimiter) => {
    if (!fileToPreview) {
      setError("Please select a file first");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", fileToPreview);

      const url = `/api/csv-datasets/preview?delimiter=${encodeURIComponent(
        delimiterOverride
      )}`;

      const response = await fetch(url, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const detail = await readErrorDetail(response);
        throw new Error(detail || "Preview failed");
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err?.message || "Failed to preview file");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setPreview(null);

    // Auto-set name from filename
    if (!name) {
      const baseName = selectedFile.name.replace(/\.(csv|tsv|txt)$/i, "");
      setName(baseName);
    }

    // Decide delimiter immediately (avoid state timing issues)
    let nextDelimiter = delimiter;
    if (selectedFile.name.toLowerCase().endsWith(".tsv")) {
      nextDelimiter = "\t";
    }
    setDelimiter(nextDelimiter);

    // Auto-preview using decided delimiter
    await handlePreview(selectedFile, nextDelimiter);
  };

  // Upload and create dataset
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }
    if (!name.trim()) {
      setError("Please enter a dataset name");
      return;
    }

    setIsUploading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name.trim());

      // NOTE: backend csv_datasets_v2.py upload does NOT declare "description".
      // It is usually ignored, but some setups may reject unknown fields.
      // If you want to keep it, keep it; otherwise comment this out.
      formData.append("description", description.trim());

      // upload endpoint expects delimiter as Form field
      formData.append("delimiter", delimiter);

      const response = await fetch("/api/csv-datasets/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const detail = await readErrorDetail(response);
        throw new Error(detail || "Upload failed");
      }

      const data = await response.json();
      setSuccess(`Dataset "${data.name}" created successfully with ${data.row_count} rows!`);

      onSuccess?.(data);

      setTimeout(() => {
        setFile(null);
        setName("");
        setDescription("");
        setPreview(null);
        setSuccess("");
      }, 1500);
    } catch (err) {
      setError(err?.message || "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        const fakeEvent = { target: { files: [droppedFile] } };
        handleFileChange(fakeEvent);
      }
    },
    [handleFileChange]
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📁 Upload CSV Dataset</h2>
        {onClose && (
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        )}
      </div>

      <div style={styles.dropZone} onDragOver={handleDragOver} onDrop={handleDrop}>
        <input
          type="file"
          accept=".csv,.tsv,.txt"
          onChange={handleFileChange}
          style={styles.fileInput}
          id="csv-file-input"
        />
        <label htmlFor="csv-file-input" style={styles.dropLabel}>
          {file ? (
            <>
              <span style={styles.fileIcon}>📄</span>
              <span style={styles.fileName}>{file.name}</span>
              <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
            </>
          ) : (
            <>
              <span style={styles.uploadIcon}>📤</span>
              <span>Drop CSV file here or click to browse</span>
              <span style={styles.hint}>Supports .csv, .tsv, .txt files</span>
            </>
          )}
        </label>
      </div>

      <div style={styles.formRow}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Dataset Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Zephyr Test Cases Q1"
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Delimiter</label>
          <select
            value={delimiter}
            onChange={(e) => {
              const v = e.target.value;
              setDelimiter(v);
              if (file) handlePreview(file, v);
            }}
            style={styles.select}
          >
            <option value=",">Comma (,)</option>
            <option value="\t">Tab</option>
            <option value=";">Semicolon (;)</option>
            <option value="|">Pipe (|)</option>
          </select>
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Test execution data exported from Zephyr Scale"
          style={styles.textarea}
          rows={2}
        />
      </div>

      {error && <div style={styles.errorMsg}>❌ {error}</div>}
      {success && <div style={styles.successMsg}>✅ {success}</div>}

      {preview && (
        <div style={styles.previewSection}>
          <div style={styles.previewHeader}>
            <h3 style={styles.previewTitle}>
              📋 Preview ({preview.total_rows} rows, {preview.columns.length} columns)
            </h3>
          </div>

          <div style={styles.columnTypes}>
            {preview.columns.map((col) => (
              <span key={col} style={styles.columnBadge}>
                <span style={styles.columnName}>{col}</span>
                <span style={styles.columnType}>{preview.detected_types?.[col] || "string"}</span>
              </span>
            ))}
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {preview.columns.map((col) => (
                    <th key={col} style={styles.th}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.map((row, idx) => (
                  <tr key={idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                    {preview.columns.map((col) => (
                      <td key={col} style={styles.td}>
                        {row[col] !== null && row[col] !== undefined
                          ? String(row[col]).substring(0, 50)
                          : <span style={styles.nullValue}>null</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.total_rows > 10 && (
            <div style={styles.moreRows}>... and {preview.total_rows - 10} more rows</div>
          )}
        </div>
      )}

      <div style={styles.actions}>
        {onClose && (
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
        )}

        <button
          onClick={() => handlePreview()}
          disabled={!file || isLoading}
          style={{ ...styles.previewBtn, opacity: !file || isLoading ? 0.5 : 1 }}
        >
          {isLoading ? "Loading..." : "🔄 Refresh Preview"}
        </button>

        <button
          onClick={handleUpload}
          disabled={!file || !name.trim() || isUploading}
          style={{ ...styles.uploadBtn, opacity: !file || !name.trim() || isUploading ? 0.5 : 1 }}
        >
          {isUploading ? "Uploading..." : "📥 Create Dataset"}
        </button>
      </div>
    </div>
  );
}

// Styles (unchanged)


// Styles
const styles = {
  container: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "900px",
    margin: "0 auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    margin: 0,
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1f2937",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#6b7280",
    padding: "4px 8px",
    borderRadius: "4px",
  },
  dropZone: {
    border: "2px dashed #d1d5db",
    borderRadius: "12px",
    padding: "40px 20px",
    textAlign: "center",
    backgroundColor: "#f9fafb",
    marginBottom: "20px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  fileInput: {
    display: "none",
  },
  dropLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    color: "#6b7280",
  },
  uploadIcon: {
    fontSize: "48px",
  },
  fileIcon: {
    fontSize: "36px",
  },
  fileName: {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: "1.1rem",
  },
  fileSize: {
    fontSize: "0.85rem",
    color: "#9ca3af",
  },
  hint: {
    fontSize: "0.85rem",
    color: "#9ca3af",
  },
  formRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
  },
  formGroup: {
    flex: 1,
    marginBottom: "16px",
  },
  label: {
    display: "block",
    marginBottom: "6px",
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "0.95rem",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "0.95rem",
    outline: "none",
    backgroundColor: "#fff",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "0.95rem",
    outline: "none",
    resize: "vertical",
    boxSizing: "border-box",
  },
  errorMsg: {
    padding: "12px 16px",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    color: "#dc2626",
    marginBottom: "16px",
    fontSize: "0.9rem",
  },
  successMsg: {
    padding: "12px 16px",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    color: "#16a34a",
    marginBottom: "16px",
    fontSize: "0.9rem",
  },
  previewSection: {
    marginTop: "20px",
    padding: "16px",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  previewHeader: {
    marginBottom: "12px",
  },
  previewTitle: {
    margin: 0,
    fontSize: "1rem",
    fontWeight: 600,
    color: "#374151",
  },
  columnTypes: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "16px",
  },
  columnBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    backgroundColor: "#e5e7eb",
    borderRadius: "16px",
    fontSize: "0.8rem",
  },
  columnName: {
    fontWeight: 500,
    color: "#1f2937",
  },
  columnType: {
    color: "#6b7280",
    fontSize: "0.75rem",
    textTransform: "uppercase",
  },
  tableWrapper: {
    overflowX: "auto",
    maxHeight: "300px",
    overflowY: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.85rem",
  },
  th: {
    padding: "10px 12px",
    textAlign: "left",
    backgroundColor: "#e5e7eb",
    fontWeight: 600,
    color: "#374151",
    position: "sticky",
    top: 0,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px",
    borderBottom: "1px solid #e5e7eb",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  trEven: {
    backgroundColor: "#ffffff",
  },
  trOdd: {
    backgroundColor: "#f9fafb",
  },
  nullValue: {
    color: "#9ca3af",
    fontStyle: "italic",
  },
  moreRows: {
    textAlign: "center",
    padding: "12px",
    color: "#6b7280",
    fontSize: "0.85rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  },
  cancelBtn: {
    padding: "10px 20px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontSize: "0.95rem",
    cursor: "pointer",
    fontWeight: 500,
  },
  previewBtn: {
    padding: "10px 20px",
    border: "1px solid #3b82f6",
    borderRadius: "8px",
    backgroundColor: "#eff6ff",
    color: "#3b82f6",
    fontSize: "0.95rem",
    cursor: "pointer",
    fontWeight: 500,
  },
  uploadBtn: {
    padding: "10px 24px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3b82f6",
    color: "#ffffff",
    fontSize: "0.95rem",
    cursor: "pointer",
    fontWeight: 600,
  },
};